// ⚡️ SOCKET CHAT OPERATIONS: Socket operations for real-time chat functionality
// Extracted from ChatPanel.js for better organization and reusability

import { useCallback, useRef } from 'react';
import { useTransition } from 'react';
import { logSecurityEvent, tsToMs, uniqueByKey } from '../utils/chatUtils';

// Custom hook for Socket chat operations
export const useSocketChat = () => {
  const [isPending, startTransition] = useTransition();
  const batchRef = useRef([]);
  const flushTimer = useRef(null);

  // 🚀 MICRO-BATCHING: Enqueue incoming messages for batched processing
  const enqueueIncoming = useCallback((validatedMsg, {
    firestoreMessages,
    processMessagesWithWorker,
    dispatch
  }) => {
    batchRef.current.push(validatedMsg);
    
    if (flushTimer.current) return; // Already scheduled
    
    flushTimer.current = window.setTimeout(async () => {
      const chunk = batchRef.current.splice(0); // Take all batched messages
      
      if (chunk.length === 0) return;
      
      try {
        // 🚀 BATCH PROCESSING: Process all messages through worker at once
        const processed = await processMessagesWithWorker(chunk, { 
          sanitize: true, 
          hash: true 
        });
        
        // 🔧 FINAL DEDUPLICATION: Apply unique-by-key before state update
        const combinedMessages = [...firestoreMessages, ...processed];
        const deduplicatedMessages = uniqueByKey(combinedMessages);
        
        // 🚀 BATCH UPDATE: Single state update for all messages
        startTransition(() => {
          dispatch({ 
            type: 'SET_FIRESTORE_MESSAGES', 
            payload: deduplicatedMessages
          });
        });
        
        // Micro-batched messages in single update
      } catch (error) {
        // Fallback: process individually with deduplication
        const fallbackMessages = [...firestoreMessages, ...chunk];
        const deduplicatedFallback = uniqueByKey(fallbackMessages);
        
        startTransition(() => {
          dispatch({ 
            type: 'SET_FIRESTORE_MESSAGES', 
            payload: deduplicatedFallback
          });
        });
      }
      
      flushTimer.current = null;
    }, 16); // One RAF-ish tick for optimal batching
  }, []);

  // Handle incoming socket messages with validation and deduplication
  const handleSocketMessage = useCallback(async (msg, {
    messageProcessing,
    sanitizeText,
    conversation,
    pendingMessages,
    firestoreMessages,
    dispatch,
    enqueueIncoming
  }) => {
    // Security: Validate and sanitize socket messages
    const validatedMsg = await messageProcessing.validateSocketMessage(msg, sanitizeText);
    if (!validatedMsg) {
      logSecurityEvent('REJECTED_SOCKET_MESSAGE', { originalMsg: msg }, 'warning');
      return;
    }
    
    // Security: Check if message belongs to current conversation
    if (conversation && conversation.id && validatedMsg.roomId !== conversation.id) {
      logSecurityEvent('MESSAGE_ROOM_MISMATCH', { 
        messageRoomId: validatedMsg.roomId, 
        currentRoomId: conversation.id 
      }, 'warning');
      return;
    }
    
    // ⚡️ SERVER SYNC: Check if this is a confirmation of our pending message
    if (validatedMsg.tempId && pendingMessages.some(pending => pending.id === validatedMsg.tempId)) {
      // ⚡️ SERVER ACKNOWLEDGMENT: Update pending message with server confirmation
      startTransition(() => {
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGE', 
          payload: { 
            id: validatedMsg.tempId, 
            status: 'sent',
            isConfirmed: true,
            serverId: validatedMsg.id
          } 
        });
      });
      return;
    }
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Deduplicate socket messages
    if (!messageProcessing.deduplicateMessage(validatedMsg)) {
      // Duplicate socket message ignored
      return;
    }
    
    // 🔧 CROSS-SOURCE DEDUPLICATION: Prevent Telegram Firestore + Socket duplicates
    const isNear = (a, b) => Math.abs(tsToMs(a) - tsToMs(b)) <= 3000; // 3s window
    
    const alreadyHaveIt = (arr, m) => arr.some(x => {
      const getText = t => (typeof t === 'string' ? t : (t?.sanitized || ''));
      return (
        getText(x.text) === getText(m.text) &&
        x.from === m.from &&
        (isNear(x.timestamp ?? x.createdAt ?? x.clientCreatedAt, m.timestamp ?? m.createdAt ?? m.clientCreatedAt) ||
         (m.isTelegramReply && x.isTelegramReply)) // extra nudge for TG replies
      );
    });
    
    // Check for clientMessageId deduplication (for Telegram messages)
    if (validatedMsg.clientMessageId) {
      if (firestoreMessages.some(m => m.clientMessageId === validatedMsg.clientMessageId) ||
          pendingMessages.some(m => m.clientMessageId === validatedMsg.clientMessageId)) {
        // Deduped cross-source message by clientMessageId
        return;
      }
    }
    
    // Check for content-based deduplication (fallback)
    if (alreadyHaveIt(firestoreMessages, validatedMsg) || alreadyHaveIt(pendingMessages, validatedMsg)) {
      // Deduped cross-source message by content
      return;
    }
    
    // 🚀 MICRO-BATCHING: Enqueue for batched processing instead of immediate dispatch
    enqueueIncoming(validatedMsg, {
      firestoreMessages,
      processMessagesWithWorker: messageProcessing.processMessagesWithWorker,
      dispatch
    });
  }, []);

  // Cleanup function for socket operations
  const cleanup = useCallback(() => {
    if (flushTimer.current) {
      clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
    batchRef.current = [];
  }, []);

  return {
    handleSocketMessage,
    enqueueIncoming,
    cleanup,
    isPending,
    startTransition
  };
};
